import React from 'react';

import { makeStyles, useTheme } from '@material-ui/core/styles';

import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';

import clsx from 'clsx';

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: '100%',
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        '& .MuiDrawer-paper':{
            left: 'auto',
            background: theme.palette.colors?.gray?.[50] || '#FAFAFA',
            borderRight: `1px solid ${theme.palette.colors?.gray?.[200] || '#E5E5E5'}`,
            position: 'relative',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            overflowX: 'hidden',
            overflowY: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        }
    },
    drawerOpen: {
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerClose: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: '56px !important',
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9) + 1,
        },
    },
    toolbar: {
        minHeight: 72,
        padding: theme.spacing(2.5, 2),
        borderBottom: `1px solid ${theme.palette.colors?.gray?.[200] || '#E5E5E5'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: theme.palette.colors?.gray?.[50] || '#FAFAFA',
        fontWeight: 600,
        fontSize: '0.9375rem',
        letterSpacing: '0.01em',
        color: theme.palette.colors?.darkBlack?.[700] || '#1A1A1A',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backdropFilter: 'blur(8px)',
        transition: 'opacity 0.2s ease, visibility 0.2s ease',
    },
    toolbarHidden: {
        opacity: 0,
        visibility: 'hidden',
        minHeight: 16,
        padding: 0,
        borderBottom: 'none',
    },
    toolbarTitle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        transition: 'opacity 0.2s ease',
    },
    list: {
        padding: theme.spacing(3, 0, 2.5, 0),
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.colors?.gray?.[300] || '#D0D0D0',
            borderRadius: '3px',
            '&:hover': {
                background: theme.palette.colors?.gray?.[400] || '#B0B0B0',
            },
        },
    },
    listItem: {
        padding: theme.spacing(0.75, 2),
        margin: theme.spacing(0.25, 1.5),
        borderRadius: 10,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        alignItems: 'center',
        minHeight: 36,
        position: 'relative',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.colors?.violet?.[50] || '#F5F0FF',
            transform: 'translateX(2px)',
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.colors?.violet?.[100] || '#E6D5FD',
            borderRadius: 10,
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: '60%',
                backgroundColor: theme.palette.colors?.violet?.[500] || '#7C3AED',
                borderRadius: '0 2px 2px 0',
            },
            '& .MuiListItemText-primary': {
                color: theme.palette.colors?.violet?.[700] || '#6D28D9',
                fontWeight: 600,
            },
            '&:hover': {
                backgroundColor: theme.palette.colors?.violet?.[150] || '#DDD6FE',
            },
        },
        '& .MuiListItemText-root': {
            marginTop: 0,
            marginBottom: 0,
            transition: 'opacity 0.2s ease, visibility 0.2s ease',
        },
    },
    listItemCollapsed: {
        padding: theme.spacing(0.75),
        margin: theme.spacing(0.5, 'auto'),
        justifyContent: 'center',
        minHeight: 'auto',
        width: 36,
        height: 36,
        borderRadius: 8,
        '& .MuiListItemText-root': {
            opacity: 0,
            visibility: 'hidden',
            width: 0,
            margin: 0,
        },
        '& .MuiListItemIcon-root': {
            minWidth: 'auto',
            marginRight: 0,
            justifyContent: 'center',
        },
        '&.Mui-selected': {
            padding: theme.spacing(0.75),
            width: 36,
            height: 36,
            '&::before': {
                display: 'none',
            },
        },
        '&:hover': {
            transform: 'none',
        },
    },
    listItemIcon: {
        minWidth: 40,
        marginRight: theme.spacing(1.5),
        justifyContent: 'flex-start',
        transition: 'margin 0.2s ease',
    },
    listItemIconCollapsed: {
        minWidth: 'auto',
        marginRight: 0,
        justifyContent: 'center',
    },
    nested: {
        paddingLeft: 0,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        transition: 'opacity 0.2s ease, visibility 0.2s ease',
    },
    nestedHidden: {
        opacity: 0,
        visibility: 'hidden',
        height: 0,
        overflow: 'hidden',
    },
    nestedItem: {
        paddingLeft: theme.spacing(5.5),
        marginLeft: theme.spacing(1.5),
        '&.Mui-selected': {
            backgroundColor: theme.palette.colors?.violet?.[100] || '#E6D5FD',
            borderRadius: 10,
            '&::before': {
                display: 'none',
            },
            '& .MuiListItemText-primary': {
                color: theme.palette.colors?.violet?.[700] || '#6D28D9',
                fontWeight: 600,
            },
            '&:hover': {
                backgroundColor: theme.palette.colors?.violet?.[150] || '#DDD6FE',
            },
        },
    },
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        transition: 'transform 0.2s ease',
    },
    iconRotate: {
        transform: 'rotate(90deg)',
    },
    footer: {
        padding: theme.spacing(1.5),
        borderTop: `1px solid ${theme.palette.colors?.gray?.[200] || '#E5E5E5'}`,
        background: theme.palette.colors?.gray?.[50] || '#FAFAFA',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        zIndex: 1,
        transition: 'padding 0.2s ease',
    },
    footerCollapsed: {
        padding: theme.spacing(1),
        justifyContent: 'center',
    },
    buttonToggle: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.colors?.violet?.[100] || '#E6D5FD',
        color: theme.palette.colors?.violet?.[700] || '#6D28D9',
        borderRadius: 10,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.colors?.violet?.[200] || '#DDD6FE',
            color: theme.palette.colors?.violet?.[800] || '#5B21B6',
        },
    },
}));

function MenuItem({ menuItem, onClickItem, level = 0, isDrawerCollapsed = false, onExpandDrawer }) {
    const classes = useStyles();
    const theme = useTheme();
    const hasChildren = menuItem.children && menuItem.children.length > 0;
    
    // Verificar recursivamente si algún descendiente está activo
    const checkActiveDeep = (children) => {
        if (!children) return false;
        return children.some(child => child.active || checkActiveDeep(child.children));
    };
    const hasActiveChild = hasChildren && checkActiveDeep(menuItem.children);
    // Expandir automáticamente solo si tiene un hijo activo o si el item mismo está activo
    const [open, setOpen] = React.useState(menuItem.active || hasActiveChild);

    React.useEffect(() => {
        if (menuItem.active || hasActiveChild) {
            setOpen(true);
        }
    }, [menuItem.active, hasActiveChild]);

    const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        // Si el drawer está colapsado, abrirlo al hacer clic en cualquier item
        if (isDrawerCollapsed) {
            onExpandDrawer && onExpandDrawer();
            return;
        }

        if (hasChildren) {
            // Si tiene hijos, expandir/colapsar Y navegar al primer hijo
            setOpen(!open);
            onClickItem(menuItem);
            return;
        }
        
        // Solo navegar si el item tiene path y no tiene hijos
        if (menuItem.path) {
            onClickItem(menuItem);
        }
    };

    const activeColor = theme.palette?.colors?.violet?.[700] || '#6D28D9';
    const inactiveColor = theme.palette?.colors?.darkBlack?.[600] || '#4A4A4A';
    const iconColor = menuItem.active 
        ? activeColor 
        : (hasChildren ? theme.palette?.colors?.gray?.[500] || '#737373' : inactiveColor);

    // Si el drawer está colapsado, no mostrar ningún item
    if (isDrawerCollapsed) {
        return null;
    }

    return (
        <>
            <ListItem 
                button 
                selected={menuItem.active}
                disableRipple
                className={clsx(
                    classes.listItem, 
                    level > 0 && classes.nestedItem,
                    isDrawerCollapsed && classes.listItemCollapsed
                )} 
                onClick={handleClick}
            >
                <ListItemIcon className={clsx(
                    classes.listItemIcon,
                    isDrawerCollapsed && classes.listItemIconCollapsed
                )}>
                    <div className={clsx(classes.iconWrapper, open && hasChildren && !isDrawerCollapsed && classes.iconRotate)}>
                        {hasChildren && !isDrawerCollapsed && (
                            <Icon 
                                className="ri-arrow-right-s-line"
                                style={{ 
                                    fontSize: 18,
                                    color: iconColor,
                                    transition: 'color 0.2s ease',
                                }} 
                            />
                        )}
                        {menuItem.icon && (!hasChildren || isDrawerCollapsed) && (
                            <Icon 
                                className={menuItem.icon}
                                style={{
                                    fontSize: isDrawerCollapsed ? 20 : 20,
                                    color: iconColor,
                                    transition: 'color 0.2s ease',
                                }}
                            />
                        )}
                    </div>
                </ListItemIcon>
                <ListItemText 
                    primary={menuItem.label} 
                    primaryTypographyProps={{
                        variant: 'body2', 
                        style: {
                            color: menuItem.active ? activeColor : inactiveColor,
                            fontWeight: menuItem.active ? 600 : (level === 0 ? 500 : 400),
                            fontSize: level === 0 ? '0.875rem' : '0.8125rem',
                            lineHeight: 1.5,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            transition: 'color 0.2s ease, font-weight 0.2s ease',
                            letterSpacing: '0.01em',
                        }
                    }}
                />
            </ListItem>
            {hasChildren && !isDrawerCollapsed && (
                <Collapse 
                    in={open} 
                    timeout={250} 
                    unmountOnExit
                    style={{
                        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <List component="div" disablePadding className={classes.nested}>
                        {menuItem.children.map((child, index) => (
                            <MenuItem 
                                key={`nested-${child.id}-${index}`}
                                menuItem={child} 
                                onClickItem={onClickItem}
                                level={level + 1}
                                isDrawerCollapsed={isDrawerCollapsed}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
}

export default function ExpandableSidebar({menu = [], onClickItem, title}) {
    const classes = useStyles();
    const theme = useTheme();
    const [open, setOpen] = React.useState(true);

    const handleDrawerToggle = () => {
        setOpen(prev => !prev);
    };

    React.useEffect(() => {
        setOpen(true);
    }, [menu.length])

    if(!menu.length){
        return null;
    }

    return (
        <div className={classes.root}>
            <Drawer
                variant="permanent"
                className={clsx(classes.drawer, {
                [classes.drawerOpen]: open,
                [classes.drawerClose]: !open,
                })}
                classes={{
                    paper: clsx({
                        [classes.drawerOpen]: open,
                        [classes.drawerClose]: !open,
                    }),
                }}
            >
                {title && (
                    <div className={clsx(classes.toolbar, !open && classes.toolbarHidden)}>
                        <span className={classes.toolbarTitle}>{title}</span>
                    </div>
                )}
                {!open && <div style={{ height: 12 }} />}
                <List className={classes.list}>
                    {menu.map((menuItem, index) => (
                        <MenuItem 
                            key={`menu-item-${menuItem.id}-${index}`}
                            menuItem={menuItem} 
                            onClickItem={onClickItem}
                            level={0}
                            isDrawerCollapsed={!open}
                            onExpandDrawer={handleDrawerToggle}
                        />
                    ))}
                </List>
                <div className={clsx(classes.footer, !open && classes.footerCollapsed)}>
                    <IconButton 
                        onClick={handleDrawerToggle} 
                        className={classes.buttonToggle}
                        size="small"
                        aria-label={open ? 'Colapsar menú' : 'Expandir menú'}
                    >
                        <Icon 
                            className={open ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'}
                            style={{ fontSize: 20 }}
                        />
                    </IconButton>
                </div>
            </Drawer>
        </div>
    );
}
